import { createFileRoute } from '@tanstack/react-router';
import type { CanvasserAnalyticsFilters } from '@/lib/queryKeys';
import { ShiftStatus } from '@signalvote/shared';
import { AnalyticsDashboard, CanvasserSelfView } from '@/components/analytics';
import { useAuth } from '@/contexts/AuthContext';
import { useCampaignStore } from '@/stores';
import { useMyStats, useShifts, useContactAnalytics } from '@/hooks/queries';

export const Route = createFileRoute('/app/analytics/')({
  component: AnalyticsDashboardRoute,
});

function AnalyticsDashboardRoute() {
  const filters = Route.useSearch() as CanvasserAnalyticsFilters;
  const { user } = useAuth();
  const { canView } = useCampaignStore();

  const hasAnalyticsCapability = canView('Analytics');

  if (hasAnalyticsCapability) {
    return <AnalyticsDashboard filters={filters} />;
  }

  return <SelfViewRoute userId={user?.id ?? ''} dateFrom={filters.dateFrom} dateTo={filters.dateTo} />;
}

function SelfViewRoute({ userId, dateFrom, dateTo }: { userId: string; dateFrom?: string; dateTo?: string }) {
  const dateFilters = { dateFrom, dateTo };
  const { data: myStats, isLoading: isLoadingStats } = useMyStats(dateFilters);
  const { data: shifts, isLoading: isLoadingShifts } = useShifts({
    claimedById: userId,
    status: ShiftStatus.Completed,
    startDate: dateFrom,
    endDate: dateTo,
  });
  const { data: contactAnalytics, isLoading: isLoadingContacts } = useContactAnalytics(userId);

  return (
    <CanvasserSelfView
      myStats={myStats}
      shifts={shifts}
      contactAnalytics={contactAnalytics}
      currentUserId={userId}
      isLoadingStats={isLoadingStats}
      isLoadingShifts={isLoadingShifts}
      isLoadingContacts={isLoadingContacts}
    />
  );
}
